<script setup lang="ts">
    import { Head, useForm } from '@inertiajs/vue3';
    import { defineAsyncComponent, ref } from 'vue';
    import axios from 'axios';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    const AppLayout = defineAsyncComponent(() => import('@/layouts/AppLayout.vue'));
    const PageCard = defineAsyncComponent(() => import('@/components/PageCard.vue'));
    import type { BreadcrumbItem } from '@/types';

    const { t } = useI18n();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.outlay',
        },
    ];

    const form = useForm({
        email: '',
        password: '',
    });

    const isSubmitting = ref(false);
    function normalizeUrl(url: string): string {
        try {
            return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `${window.location.protocol}//${url}`;
        } catch (e) {
            return url;
        }
    }

    function redirectTo(url: string) {
        const target = normalizeUrl(url);
        try {
            window.location.href = target;
            return;
        } catch (e) {}

        try {
            window.location.assign(target);
            return;
        } catch (e) {}

        try {
            window.open(target, '_self');
        } catch (e) {}
    }

    const onSubmit = async () => {
        if (isSubmitting.value) return;
        isSubmitting.value = true;

        try {
            const { data } = await axios.post(route('outlay.user-connect.store'), {
                email: form.email,
                password: form.password,
            });

            if (data?.url) {
                redirectTo(data.url as string);
                return;
            }

            if (data?.redirect) {
                redirectTo(data.redirect as string);
                return;
            }

            if (data?.pjax_redirect) {
                redirectTo(data.pjax_redirect as string);
                return;
            }

            toast.error(t('forms.system_error'));
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errors?.incorrect_credentials
                ?? error?.response?.data?.message
                ?? t('forms.system_error');
            toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage);
        } finally {
            isSubmitting.value = false;
        }
    };
</script>

<template>
    <Head :title="$t('outlay.connect.title')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard>
            <div class="max-w-xl mx-auto">
                <div class="text-center text-xl font-semibold">
                    {{ t('outlay.connect.title') }}
                </div>
                <div class="text-center text-muted-foreground mt-2">
                    {{ t('outlay.connect.subtitle') }}
                </div>
                <div class="flex justify-center mt-6 mb-8">
                    <img src="/img/logo-outlay.svg" alt="Outlay" class="h-8" />
                </div>

                <form @submit.prevent="onSubmit" class="grid gap-4">
                    <div class="grid gap-1.5">
                        <Label for="outlay_email">{{ t('auth.email') }}</Label>
                        <Input
                            id="outlay_email"
                            v-model="form.email"
                            type="email"
                            required
                        />
                    </div>

                    <div class="grid gap-1.5">
                        <Label for="outlay_password">{{ t('auth.password') }}</Label>
                        <Input
                            id="outlay_password"
                            v-model="form.password"
                            type="password"
                            required
                        />
                    </div>

                    <Button
                        :disabled="isSubmitting"
                        type="submit"
                        width="full"
                        class="mt-2"
                    >
                        {{ t('auth.login_txt') }}
                    </Button>
                </form>
            </div>
        </PageCard>
    </AppLayout>
</template>


